<template>
  <div class="w-full h-full">
    <div
      class="gap-4 w-full h-full relative overflow-hidden rounded-3xl"
      v-if="item?.id || item?.shimmer"
      :class="{
        shimmerElementRoundedDefault: item.shimmer
      }"
      @click="showDetails(item)"
    >
      <div
        class="relative bg-slate-100 overflow-hidden w-full p-[35%] ripple border border-transparent rounded-3xl"
      >
        <div class="absolute w-full h-full left-0 top-0">
          <div
            class="absolute w-full h-full duration-300 object-cover shimmerElementRoundedDefault"
          ></div>
          <ImgComponent
            class="absolute w-full h-full scale-[1.2] duration-300 object-cover"
            src="/images/placeholders/restaurant-dishes.png"
            alt=""
            :class="{ 'opacity-0': !showPlaceHolder }"
          />
          <ImgComponent
            class="relative w-full h-full object-cover scale-[1.2]"
            :src="item.principalImageUrl"
            alt=""
          />
        </div>
      </div>
      <div
        class="absolute w-10 h-10 right-2 top-2 rounded-full ripple z-2"
        @click.stop="toggleFavorite"
      >
        <div
          class="w-full h-full rounded-full border-white border-[6px] bg-white p-1"
        >
          <ImgComponent
            class="w-full h-full"
            :src="
              isFavorite
                ? '/images/all/heart-primary.svg'
                : '/images/all/heart-outline.svg'
            "
            alt=""
          />
        </div>
      </div>
      <!-- Badges -->
      <div class="absolute left-2 top-2 flex flex-col gap-1 items-start">
        <!-- Restaurant closed indicator -->
        <div
          v-if="item.isRestaurantOpen === false && item.restaurant"
          class="px-2 py-1 rounded-full text-xs font-medium bg-red-500 text-white"
        >
          Fermé
        </div>
        <!-- Unavailable badge -->
        <div
          v-if="!item.isPublished && item.isRestaurantOpen !== false"
          class="px-2 py-1 rounded-full text-xs font-medium bg-gray-500 text-white"
        >
          Indisponible
        </div>

        <!-- Promotion badge -->
        <div
          v-if="item.promotion && item.isPublished"
          class="px-2 py-1 rounded-full text-xs font-bold bg-primary text-white flex items-center gap-1 shadow-md shadow-primary/30"
        >
          <template v-if="item.promotion.discountType === 'percentage'">
            -{{ item.promotion.discount }}%
          </template>
          <template v-else-if="item.promotion.discountType === 'fixed'">
            -{{ item.promotion.discount }} F
          </template>
        </div>

        <!-- Top seller badge -->
        <div
          v-if="item.isTop && item.isPublished"
          class="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white flex items-center gap-1 shadow-sm"
        >
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
            />
          </svg>
          Top
        </div>
        <!-- Favorite badge -->
        <div
          v-if="item.isFavorite && item.isPublished"
          class="px-2 py-1 rounded-full text-xs font-medium bg-pink-500 text-white flex items-center gap-1 shadow-sm"
        >
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
              clip-rule="evenodd"
            />
          </svg>
          Favori
        </div>
      </div>
      <div class="top-0 left-0 ripple absolute w-full h-full"></div>
      <div
        class="w-full flex flex-col px-4 pb-1 absolute bottom-0 liquidGlassCard border-x-0 border-b-0"
      >
        <div class="flex items-center">
          <div class="font-bold title-color w-full truncate text-lg">
            {{ item.name }}
          </div>
        </div>

        <div class="flex items-center gap-2">
          <div class="text-primary font-bold truncate">
            {{
              item.restaurant?.name?.name ||
              item.restaurant?.name ||
              'Restaurant'
            }}
          </div>
          <div
            class="bg-[#F4F4F5] px-3 py-1 rounded-xl text-xs inline-block whitespace-nowrap mr-2"
          >
            {{ item.category }}
          </div>
        </div>
        <div class="flex gap-2 pt-0 items-center">
          <div class="flex flex-col">
            <!-- Price Display -->
            <div class="flex items-end gap-2">
              <div
                class="whitespace-nowrap flex gap-1 title-color font-bold"
                :class="
                  item.promotion ? 'text-xl underline' : 'text-xl underline'
                "
              >
                {{
                  item.promotion ? item.promotion.promotionalPrice : item.price
                }}
                CFA
              </div>
              <div
                v-if="item.promotion"
                class="text-xs text-slate-400 line-through font-medium mb-[3px]"
              >
                {{ item.price }} CFA
              </div>
            </div>
          </div>

          <div class="w-full relative flex ">
            <div>
              <i
                class="text- text-primary"
                v-for="el in 5"
                :key="el"
                :class="{
                  'fas fa-star': el <= 4,
                  'far fa-star': el > 4
                }"
              ></i>
            </div>
          </div>
          <div
            @click.stop="addToCart"
            class="ripple z-[2] text-2xl rounded-full min-w-9 w-9 h-9 p-1.5 flex items-center justify-center transition-all duration-300"
            :class="{
              'text-white bg-primary shadow-lg shadow-primary/30':
                isAddedToCart,
              'text-primary bg-primary-50': !isAddedToCart
            }"
          >
            <ColorImg
              v-if="!isAddedToCart"
              src="/images/all/add-to-cart.svg"
              class="bg-primary w-full h-full"
              alt=""
            />
            <ColorImg
              v-else
              src="/images/all/check-white.svg"
              class="w-full h-full p-0.5 bg-white"
              alt=""
            />
          </div>
        </div>
      </div>

      <div></div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import constants from '@core/utils/constants';

export default defineComponent({
  name: 'DishCard',
  props: {
    item: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      showPlaceHolder: false
    };
  },
  async mounted() {
    setTimeout(() => {
      this.showPlaceHolder = true;
    }, 5000);
  },
  beforeUnmount() {},
  computed: {
    restaurantData() {
      // Use enriched restaurant data from API
      return this.item?.restaurant || null;
    },
    isFavorite() {
      const favoriteDishes =
        window.$stores?.user?.state?.current?.favoriteDishes || [];
      return favoriteDishes.includes(this.item.id);
    },
    isAddedToCart() {
      const cartItems = window.$stores?.cart?.state?.cart?.items || [];
      return cartItems.some((item) => item.dishId === this.item.id);
    },
    cartItemId() {
      const cartItems = window.$stores?.cart?.state?.cart?.items || [];
      const found = cartItems.find((item) => item.dishId === this.item.id);
      return found ? found.id : null;
    }
  },
  methods: {
    showDetails(dish) {
      if (!dish || !dish?.id) return;
      this.$bus.emit('dish.SHOW_DISH_DETAIL', {
        id: dish.id
      });
    },
    toggleFavorite() {
      if (!this.item?.id) return;
      this.$bus.emit(constants.EVENTS.USER.FAVORITE_TOGGLE_DISH, {
        dishId: this.item.id,
        isFavorite: this.isFavorite
      });
    },
    async addToCart() {
      if (!this.item || !this.item.id) {
        console.error('Invalid item for adding to cart');
        return;
      }

      // Toggle logic: If already in cart, remove it
      if (this.isAddedToCart && this.cartItemId) {
        this.$bus.emit(constants.EVENTS.CART.ITEMS.REMOVE, {
          itemId: this.cartItemId
        });
        return;
      }

      // Emit cart add event to trigger API call and store update
      console.log('🚀 ~ this.item:', this.item);
      this.$bus.emit(constants.EVENTS.CART.ITEMS.ADD, {
        restaurantId:
          this.item.storeId ||
          this.item.restaurant?.name?.id ||
          this.item.restaurant?.id,
        restaurant: this.item.restaurant,
        restaurantName:
          this.item.restaurant?.name?.name ||
          this.item.restaurant?.name ||
          this.item.storeName ||
          'Restaurant',
        dishId: this.item.id,
        title: this.item.name,
        name: this.item.name,
        price: this.item.promotion
          ? this.item.promotion.promotionalPrice
          : this.item.price,
        originalPrice: this.item.promotion ? this.item.price : undefined,
        promotion: this.item.promotion || undefined,
        quantity: 1,
        currency: this.item.currency || 'XOF',
        description: this.item.description,
        category: this.item.category,
        categoryId: this.item.categoryId,
        principalImageUrl: this.item.principalImageUrl || this.item.image,
        options: [],
        drinks: [],
        fullOptions: this.item.options,
        fullAccompaniments: this.item.accompaniments
      });
    }
  }
});
</script>
